import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Container,
  Table,
  Form,
  Modal,
} from "react-bootstrap";
import Swal from 'sweetalert2';
import { AppDispatch, RootState } from "../../store";
import { useTranslation } from "react-i18next";
import companiesService, { Company } from "../../features/companies/companiesService";
import usersService from "../../features/users/usersService";
import Pagination from "../../components/common/Pagination";
import CompanyFormModal from "../companies/components/CompanyFormModal";
import { useTheme } from "../../context/ThemeContext";
import { Icon } from "../../components/common/Icon";
import {
  FaBuilding,
  FaPlus,
  FaCheckCircle,
  FaPauseCircle,
  FaEye,
  FaEdit,
  FaUsers,
  FaBriefcase,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaTrash,
  FaTimes,
  FaCheck
} from "react-icons/fa";

const ClientCompaniesPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  const [clientCompanies, setClientCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showAssignUsersModal, setShowAssignUsersModal] = useState(false);
  const [companyUsers, setCompanyUsers] = useState<any[]>([]);
  const [availableMarketingSpecialists, setAvailableMarketingSpecialists] = useState<any[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingSpecialists, setIsLoadingSpecialists] = useState(false);
  const [selectedUsersToAssign, setSelectedUsersToAssign] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // Client companies'leri yükle
  useEffect(() => {
    loadClientCompanies();
  }, []);

  const loadClientCompanies = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const companies = await companiesService.getMyClientCompanies();
      setClientCompanies(companies);
    } catch (err) {
      console.error("Error loading client companies:", err);
      setError("Failed to load client companies. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleModalHide = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedCompany(null);
    loadClientCompanies();
  };

  const handleViewCompany = (company: Company) => {
    setSelectedCompany(company);
    setShowDetailModal(true);
  };

  const handleEditCompany = (company: Company) => {
    setSelectedCompany(company);
    setShowEditModal(true);
  };

  const handleViewUsers = async (company: Company) => {
    setSelectedCompany(company);
    setShowUsersModal(true);
    await loadCompanyUsers(company.id);
  };

  const loadCompanyUsers = async (companyId: number) => {
    try {
      setIsLoadingUsers(true);
      const users = await companiesService.getCompanyUsers(companyId);
      setCompanyUsers(users);
    } catch (err) {
      console.error("Error loading company users:", err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load company users.',
      });
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleAssignUsers = async (company: Company) => {
    setSelectedCompany(company);
    setShowAssignUsersModal(true);
    await loadAvailableMarketingSpecialists();
  };

  const loadAvailableMarketingSpecialists = async () => {
    try {
      setIsLoadingSpecialists(true);
      // Use the dedicated endpoint to fetch marketing specialists for the current agency
      const specialists = await usersService.getMyAgencyMarketingSpecialists();
      setAvailableMarketingSpecialists(specialists);
    } catch (err) {
      console.error("Error loading marketing specialists:", err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load marketing specialists.',
      });
    } finally {
      setIsLoadingSpecialists(false);
    }
  };

  const handleUserSelectionChange = (userId: number, isSelected: boolean) => {
    if (isSelected) {
      setSelectedUsersToAssign(prev => [...prev, userId]);
    } else {
      setSelectedUsersToAssign(prev => prev.filter(id => id !== userId));
    }
  };

  const handleAssignUsersSubmit = async () => {
    if (!selectedCompany || selectedUsersToAssign.length === 0) return;

    try {
      await companiesService.addUsersToCompany(selectedCompany.id, { user_ids: selectedUsersToAssign });
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Users assigned successfully!',
      });
      setShowAssignUsersModal(false);
      setSelectedUsersToAssign([]);
      await loadCompanyUsers(selectedCompany.id);
    } catch (err) {
      console.error("Error assigning users:", err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to assign users.',
      });
    }
  };

  const handleRemoveUserFromCompany = async (userId: number) => {
    if (!selectedCompany) return;

    const result = await Swal.fire({
      title: 'Remove User',
      text: 'Are you sure you want to remove this user from the company?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove!'
    });

    if (result.isConfirmed) {
      try {
        await companiesService.removeUsersFromCompany(selectedCompany.id, { user_ids: [userId] });
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'User removed successfully!',
        });
        await loadCompanyUsers(selectedCompany.id);
      } catch (err) {
        console.error("Error removing user:", err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to remove user.',
        });
      }
    }
  };

  const handleCloseUsersModal = () => {
    setShowUsersModal(false);
    setSelectedCompany(null);
    setCompanyUsers([]);
  };

  const handleCloseAssignUsersModal = () => {
    setShowAssignUsersModal(false);
    setSelectedUsersToAssign([]);
  };

  // Pagination
  const totalCount = clientCompanies.length;
  const totalPages = Math.ceil(totalCount / limit);
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedCompanies = clientCompanies.slice(startIndex, endIndex);

  // Stats
  const activeCount = clientCompanies.filter(company => company.is_active).length;
  const inactiveCount = totalCount - activeCount;

  if (isLoading) {
    return (
      <div className="client-companies-page">
        <Container fluid>
          <div className="modern-loading-state">
            <div className="loading-spinner">
              <Icon icon={FaBuilding} className="fa-spin" />
            </div>
            <h5 className="loading-title">Loading Client Companies</h5>
            <p className="loading-subtitle">Please wait while we fetch your data...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="client-companies-page">
      <Container fluid>
        {/* Modern Header */}
        <div className="modern-header slide-in-down">
          <div className="header-content">
            <div className="header-left">
              <div className="header-icon-wrapper">
                <div className="header-icon">
                  <Icon icon={FaBuilding} />
                </div>
              </div>
              <div className="header-text">
                <h1 className="header-title">Client Companies</h1>
                <p className="header-subtitle">Manage your client companies and their teams</p>
              </div>
            </div>
            <div className="header-actions">
              <Button
                className="btn-modern btn-primary"
                onClick={() => setShowCreateModal(true)}
              >
                <Icon icon={FaPlus} className="me-2" />
                <span>Add Company</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Modern Stats Grid */}
        <div className="modern-stats-grid slide-in-up animate-delay-1">
          <div className="stat-modern-card">
            <div className="stat-modern-icon primary">
              <Icon icon={FaBuilding} />
            </div>
            <div className="stat-modern-content">
              <div className="stat-modern-value">{totalCount}</div>
              <div className="stat-modern-label">Total Companies</div>
            </div>
          </div>

          <div className="stat-modern-card">
            <div className="stat-modern-icon active">
              <Icon icon={FaCheckCircle} />
            </div>
            <div className="stat-modern-content">
              <div className="stat-modern-value">{activeCount}</div>
              <div className="stat-modern-label">Active Companies</div>
            </div>
          </div>

          <div className="stat-modern-card">
            <div className="stat-modern-icon inactive">
              <Icon icon={FaPauseCircle} />
            </div>
            <div className="stat-modern-content">
              <div className="stat-modern-value">{inactiveCount}</div>
              <div className="stat-modern-label">Inactive Companies</div>
            </div>
          </div>
        </div>

        {/* Modern Client Companies Table */}
        <div className="modern-table-container slide-in-up animate-delay-2">
          <div className="table-header-modern">
            <div className="table-header-left">
              <h3 className="table-title">Client Companies</h3>
              <span className="table-subtitle">{totalCount} companies total</span>
            </div>
            <div className="table-header-right">
              <Form.Select
                size="sm"
                value={limit}
                onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                className="modern-select"
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </Form.Select>
            </div>
          </div>

          <div className="table-content-modern">
            {paginatedCompanies.length === 0 ? (
              <div className="modern-empty-state">
                <div className="empty-icon-modern">
                  <Icon icon={FaBuilding} />
                </div>
                <h4 className="empty-title-modern">No Client Companies</h4>
                <p className="empty-description-modern">
                  Start managing your client companies by adding the first one
                </p>
                <Button
                  className="btn-modern btn-primary"
                  onClick={() => setShowCreateModal(true)}
                >
                  <Icon icon={FaPlus} className="me-2" />
                  <span>Add First Company</span>
                </Button>
              </div>
            ) : (
              <div className="modern-table-wrapper table-responsive">
                <Table className="modern-table">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}></th>
                      <th>{t("company_name")}</th>
                      <th>{t("industry")}</th>
                      <th>{t("contact")}</th>
                      <th>{t("location")}</th>
                      <th>{t("status")}</th>
                      <th style={{ width: 120 }}>{t("actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCompanies.map((company) => (
                      <tr key={company.id}>
                        <td>
                          <div className="company-avatar-modern">
                            <div className="avatar-modern circle">
                              {company.logo_url ? (
                                <img src={company.logo_url} alt={company.company_name} />
                              ) : (
                                <Icon icon={FaBuilding} />
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="info-cell-modern">
                            <div className="cell-title">{company.company_name}</div>
                            {company.company_size && (
                              <div className="cell-subtitle">
                                <Icon icon={FaUsers} className="me-1" />
                                {company.company_size}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          {company.industry && (
                            <div className="company-industry-modern">
                              <Icon icon={FaBriefcase} className="me-1" />
                              {company.industry}
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="company-contact-modern">
                            {company.email && (
                              <div className="contact-item">
                                <Icon icon={FaEnvelope} className="me-1" />
                                <a href={`mailto:${company.email}`}>{company.email}</a>
                              </div>
                            )}
                            {company.phone && (
                              <div className="contact-item">
                                <Icon icon={FaPhone} className="me-1" />
                                <a href={`tel:${company.phone}`}>{company.phone}</a>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          {company.city && company.country && (
                            <div className="company-location-modern">
                              <Icon icon={FaMapMarkerAlt} className="me-1" />
                              {company.city}, {company.country}
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="status-badge-modern">
                            {company.is_active ? (
                              <span className="status-badge-modern success">
                                <Icon icon={FaCheckCircle} />
                                Active
                              </span>
                            ) : (
                              <span className="status-badge-modern secondary">
                                <Icon icon={FaPauseCircle} />
                                Inactive
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="action-buttons-modern">

                            <button
                              className="action-btn-modern edit"
                              onClick={() => handleEditCompany(company)}
                              title="Edit Company"
                              aria-label="Edit Company"
                            >
                              <Icon icon={FaEdit} />
                            </button>
                            <button
                              className="action-btn-modern users"
                              onClick={() => handleViewUsers(company)}
                              title="View & Manage Users"
                              aria-label="View & Manage Users"
                            >
                              <Icon icon={FaUsers} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>

          {/* Modern Pagination */}
          {totalCount > 0 && totalPages > 1 && (
            <div className="modern-pagination">
              <div className="pagination-info-modern">
                Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, totalCount)} of {totalCount} companies
              </div>
              <Pagination
                currentPage={page}
                totalCount={totalCount}
                pageSize={limit}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>

        {/* Create/Edit/Detail Modals */}
        <CompanyFormModal
          show={showCreateModal}
          onHide={handleModalHide}
          initialCompanyType="client"
        />
        {selectedCompany && (
          <CompanyFormModal
            show={showEditModal}
            onHide={handleModalHide}
            company={selectedCompany}
          />
        )}

        {/* Modern Company Users Management Modal */}
        <Modal
          show={showUsersModal}
          onHide={handleCloseUsersModal}
          size="xl"
          centered
          className="modern-modal"
        >
          <Modal.Header closeButton className="modern-modal-header">
            <div className="modal-header-content">
              <div className="modal-header-left">
                <div className="modal-header-icon">
                  <Icon icon={FaUsers} />
                </div>
                <div className="modal-header-text">
                  <h4 className="modal-title">Manage Users</h4>
                  <p className="modal-subtitle">{selectedCompany?.company_name}</p>
                </div>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body className="modern-modal-body">
            {isLoadingUsers ? (
              <div className="modern-loading-state">
                <div className="loading-spinner">
                  <Icon icon={FaBuilding} className="fa-spin" />
                </div>
                <h5 className="loading-title">Loading Users</h5>
                <p className="loading-subtitle">Please wait while we fetch the user data...</p>
              </div>
            ) : (
              <div className="users-management-content">
                {/* Header Section */}
                <div className="section-header-modern">
                  <div className="section-header-left">
                    <h5 className="section-title">Marketing Specialists</h5>
                    <p className="section-subtitle">
                      Manage users assigned to this client company
                    </p>
                  </div>
                  <div className="section-header-right">
                    <Button
                      className="btn-modern btn-primary"
                      onClick={() => {
                        if (selectedCompany) {
                          handleAssignUsers(selectedCompany);
                        }
                      }}
                    >
                      <Icon icon={FaPlus} className="me-2" />
                      <span>Assign Users</span>
                    </Button>
                  </div>
                </div>

                {(() => {
                  const filteredUsers = companyUsers.filter((user) => {
                    const userRoles = user.roles || [];
                    return userRoles.some((role: any) => role.key === "marketing_specialist");
                  });
                  return filteredUsers.length === 0;
                })() ? (
                  <div className="modern-empty-state">
                    <div className="empty-icon-modern">
                      <Icon icon={FaUsers} />
                    </div>
                    <h4 className="empty-title-modern">No Marketing Specialists</h4>
                    <p className="empty-description-modern">
                      No marketing specialists are currently assigned to this company
                    </p>
                    <Button
                      className="btn-modern btn-primary"
                      onClick={() => {
                        if (selectedCompany) {
                          handleAssignUsers(selectedCompany);
                        }
                      }}
                    >
                      <Icon icon={FaPlus} className="me-2" />
                      <span>Assign Marketing Specialists</span>
                    </Button>
                  </div>
                ) : (
                  <div className="modern-table-container">
                    <div className="table-responsive">
                      <Table className="modern-table">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th style={{ width: 100 }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {companyUsers
                            .filter((user) => {
                              const userRoles = user.roles || [];
                              return userRoles.some((role: any) => role.key === "marketing_specialist");
                            })
                            .map((user) => (
                              <tr key={user.user_id}>
                                <td>
                                  <div className="info-cell-modern">
                                    <div className="cell-title">{user.full_name || user.username}</div>
                                    <div className="cell-subtitle">@{user.username}</div>
                                  </div>
                                </td>
                                <td>
                                  <div className="user-email-modern">
                                    <a href={`mailto:${user.email}`}>
                                      {user.email}
                                    </a>
                                  </div>
                                </td>
                                <td>
                                  <div className="role-badges-modern">
                                    <span className="role-badge">
                                      <Icon icon={FaBriefcase} className="me-1" />
                                      Marketing Specialist
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <div className="status-badge-modern">
                                    {user.status === "active" ? (
                                      <span className="status-badge-modern success">
                                        <Icon icon={FaCheckCircle} />
                                        Active
                                      </span>
                                    ) : (
                                      <span className="status-badge-modern secondary">
                                        <Icon icon={FaPauseCircle} />
                                        Inactive
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div className="action-buttons-modern">
                                    <button
                                      className="action-btn-modern delete"
                                      onClick={() => handleRemoveUserFromCompany(user.user_id)}
                                      title="Remove from company"
                                      aria-label="Remove from company"
                                    >
                                      <Icon icon={FaTrash} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="modern-modal-footer">
            <div className="modal-footer-content">
              <Button
                className="btn-modern btn-secondary"
                onClick={handleCloseUsersModal}
              >
                <Icon icon={FaTimes} className="me-2" />
                <span>Close</span>
              </Button>
            </div>
          </Modal.Footer>
        </Modal>

        {/* Modern Assign Users Modal */}
        <Modal
          show={showAssignUsersModal}
          onHide={handleCloseAssignUsersModal}
          size="lg"
          centered
          className="modern-modal"
        >
          <Modal.Header closeButton className="modern-modal-header">
            <div className="modal-header-content">
              <div className="modal-header-left">
                <div className="modal-header-icon">
                  <Icon icon={FaPlus} />
                </div>
                <div className="modal-header-text">
                  <h4 className="modal-title">Assign Marketing Specialists</h4>
                  <p className="modal-subtitle">{selectedCompany?.company_name}</p>
                </div>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body className="modern-modal-body">
            {isLoadingSpecialists ? (
              <div className="modern-loading-state">
                <div className="loading-spinner">
                  <Icon icon={FaBuilding} className="fa-spin" />
                </div>
                <h5 className="loading-title">Loading Specialists</h5>
                <p className="loading-subtitle">Please wait while we fetch available specialists...</p>
              </div>
            ) : availableMarketingSpecialists.length === 0 ? (
              <div className="modern-empty-state">
                <div className="empty-icon-modern">
                  <Icon icon={FaUsers} />
                </div>
                <h4 className="empty-title-modern">No Specialists Available</h4>
                <p className="empty-description-modern">
                  No marketing specialists available in your agency. Create marketing specialists in My Team page first.
                </p>
              </div>
            ) : (
              <div className="assign-users-content">
                <div className="section-header-modern">
                  <h5 className="section-title">Select Specialists</h5>
                  <p className="section-subtitle">
                    Select marketing specialists to assign to this client company
                  </p>
                </div>
                <div className="modern-table-container">
                  <div className="table-responsive">
                    <Table className="modern-table">
                      <thead>
                        <tr>
                          <th style={{ width: 80, textAlign: 'center' }}>
                            <div className="checkbox-wrapper">
                              <Form.Check
                                type="checkbox"
                                className="modern-checkbox select-all-checkbox"
                                checked={selectedUsersToAssign.length === availableMarketingSpecialists.length && availableMarketingSpecialists.length > 0}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedUsersToAssign(availableMarketingSpecialists.map(u => u.id));
                                  } else {
                                    setSelectedUsersToAssign([]);
                                  }
                                }}
                              />
                            </div>
                          </th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Status</th>
                          <th>Current Assignment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {availableMarketingSpecialists.map((specialist) => {
                          const isAlreadyAssigned = companyUsers.some((user: any) => user.user_id === specialist.id);

                          return (
                            <tr
                              key={specialist.id}
                              className={`${isAlreadyAssigned ? "already-assigned" : ""} ${selectedUsersToAssign.includes(specialist.id) ? "selected" : ""}`}
                            >
                              <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                <div className="checkbox-wrapper">
                                  <Form.Check
                                    type="checkbox"
                                    className="modern-checkbox user-checkbox"
                                    checked={selectedUsersToAssign.includes(specialist.id)}
                                    onChange={(e) => handleUserSelectionChange(specialist.id, e.target.checked)}
                                    disabled={isAlreadyAssigned}
                                  />
                                </div>
                              </td>
                              <td>
                                <div className="info-cell-modern">
                                  <div className="cell-title">{specialist.full_name || specialist.username}</div>
                                  <div className="cell-subtitle">@{specialist.username}</div>
                                </div>
                              </td>
                              <td>
                                <div className="user-email-modern">
                                  <a href={`mailto:${specialist.email}`}>
                                    {specialist.email}
                                  </a>
                                </div>
                              </td>
                              <td>
                                <div className="status-badge-modern">
                                  {specialist.status === "active" ? (
                                    <span className="status-badge-modern success">
                                      <Icon icon={FaCheckCircle} />
                                      Active
                                    </span>
                                  ) : (
                                    <span className="status-badge-modern secondary">
                                      <Icon icon={FaPauseCircle} />
                                      Inactive
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="assignment-status">
                                  {isAlreadyAssigned ? (
                                    <span className="assignment-badge assigned">
                                      <Icon icon={FaCheckCircle} className="me-1" />
                                      Already Assigned
                                    </span>
                                  ) : (
                                    <span className="assignment-badge not-assigned">
                                      <Icon icon={FaPlus} className="me-1" />
                                      Not Assigned
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="modern-modal-footer">
            <div className="modal-footer-content">
              <Button
                className="btn-modern btn-secondary"
                onClick={handleCloseAssignUsersModal}
              >
                <Icon icon={FaTimes} className="me-2" />
                <span>Cancel</span>
              </Button>
              <Button
                className="btn-modern btn-primary"
                onClick={handleAssignUsersSubmit}
                disabled={selectedUsersToAssign.length === 0}
              >
                <Icon icon={FaCheck} className="me-2" />
                <span>Assign Selected ({selectedUsersToAssign.length})</span>
              </Button>
            </div>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default ClientCompaniesPage;
